import React from 'react';
import { createPortal } from 'react-dom';

import { UserCircleIcon } from '@heroicons/react/24/outline';
import { PropsWithChildren } from "react";
import { IBrand, useBrands, useLogin } from '../../../services';
import { Avatar } from '../avatar/Avatar.component';

export interface FlyoutProps {
    title: string;
    icon: any;
    showIcon?: boolean;
    buttonContent?: React.ReactNode;
    buttonClassName?: string;
    closeRef?: React.MutableRefObject<(() => void) | null>;
    /** Overrides the default `w-64` panel box. Use for wider panels (e.g. tabbed content). */
    panelClassName?: string;
    /**
     * Children are wrapped in a `<ul>` by default, which suits menu lists. Set
     * false for panels containing arbitrary block content — a `<div>` inside a
     * `<ul>` is invalid markup.
     */
    wrapList?: boolean;
    /** Hides the decorative pointer arrow, which reads oddly on wide panels. */
    hideArrow?: boolean;
}

export const Flyout: React.FunctionComponent<PropsWithChildren<FlyoutProps>> = (props: PropsWithChildren<FlyoutProps>): JSX.Element => {
    const { children, title, showIcon = true, buttonContent, buttonClassName, closeRef, panelClassName, wrapList = true, hideArrow = false } = props;

    const [,,singleBrand,, brandKeyFunctions] = useBrands();
    const [brand, setBrand] = React.useState<IBrand | null>(null);
    const [user] = useLogin();
    const [show, setShow] = React.useState(false);

    React.useEffect(() => {
        if (closeRef) closeRef.current = () => setShow(false);
    }, [closeRef]);

    React.useEffect(() => {
        setBrand(null);
        if (user) {
            brandKeyFunctions.getBrand(user.brand_id);
        }
    }, []);

    React.useEffect(() => {
        if (singleBrand) {
            setBrand(singleBrand);
        }
    }, [singleBrand]);

    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const panelRef = React.useRef<HTMLDivElement | null>(null);
    const [anchor, setAnchor] = React.useState<{ top: number; left: number; openLeft: boolean; openUp: boolean; corner: 'lt' | 'rt' | 'lb' | 'rb' }>({ top: 0, left: 0, openLeft: false, openUp: false, corner: 'lt' });
    const [panelPos, setPanelPos] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const computeAnchor = React.useCallback(() => {
        try {
            if (!buttonRef.current) return;
            const rect = buttonRef.current.getBoundingClientRect();
            const cx = (typeof window !== 'undefined' ? window.innerWidth : 0) / 2;
            const cy = (typeof window !== 'undefined' ? window.innerHeight : 0) / 2;
            const ex = rect.left + rect.width / 2;
            const ey = rect.top + rect.height / 2;
            const openLeft = ex > cx; // if trigger is on right half, open left toward center
            const openUp = ey > cy;   // if trigger is on lower half, open up toward center
            const baseX = openLeft ? rect.left : rect.right;
            const baseY = openUp ? rect.top : rect.bottom;
            setAnchor({ top: baseY, left: baseX, openLeft, openUp, corner: (openLeft ? (openUp ? 'lb' : 'lt') : (openUp ? 'rb' : 'rt')) });
        } catch { /* noop */ }
    }, []);

    React.useEffect(() => {
        const onResize = () => { if (show) computeAnchor(); };
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', onResize);
            window.addEventListener('scroll', onResize, true);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', onResize);
                window.removeEventListener('scroll', onResize, true);
            }
        };
    }, [computeAnchor, show]);

    React.useLayoutEffect(() => {
        if (!show) return;
        if (!panelRef.current) return;
        const gap = 16; // space between button and panel
        const margin = 8; // viewport margin
        const rect = panelRef.current.getBoundingClientRect();
        let x = anchor.left + (anchor.openLeft ? -rect.width - gap : gap);
        let y = anchor.top + (anchor.openUp ? -rect.height - gap : gap);
        const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 0;
        // clamp inside viewport
        x = Math.min(Math.max(margin, x), Math.max(margin, vw - rect.width - margin));
        y = Math.min(Math.max(margin, y), Math.max(margin, vh - rect.height - margin));
        setPanelPos({ x, y });
    }, [show, anchor]);

    const handleButtonClick = () => {
        if (show) {
            setShow(false);
            return;
        }
        computeAnchor();
        setShow(true);
    };

    React.useEffect(() => {
        if (!show) return;
        const onDocPointer = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node | null;
            if (panelRef.current && panelRef.current.contains(target as Node)) return;
            if (buttonRef.current && buttonRef.current.contains(target as Node)) return;
            setShow(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setShow(false);
        };
        document.addEventListener('mousedown', onDocPointer, true);
        document.addEventListener('touchstart', onDocPointer, true);
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('mousedown', onDocPointer, true);
            document.removeEventListener('touchstart', onDocPointer, true);
            document.removeEventListener('keydown', onKey, true);
        };
    }, [show]);

    return (
        <>
            <div className="relative inline-block rounded-md dark:text-gray-100 text-gray-600">
                <button
                    ref={buttonRef}
                    onClick={handleButtonClick}
                    type="button"
                    aria-expanded={show}
                    aria-haspopup="true"
                    className={`inline-flex items-center gap-x-1 text-sm font-medium leading-6 outline-none px-3 py-2 rounded-md hover:bg-surface-muted dark:hover:bg-stone-800 ${buttonClassName ?? ''}`}
                >
                    {buttonContent ? (
                        buttonContent
                    ) : (
                        <>
                            {showIcon && (
                                <>
                                    {brand && brand.brand_logo ? (
                                        <Avatar square={false} src={brand.brand_logo} height='25px' width='25px'></Avatar>
                                    ) : (
                                        <UserCircleIcon key={"profile-icon"} className="h-5 w-5 shrink-0" />
                                    )}
                                </>
                            )}
                            <span className={`text-sm whitespace-nowrap ${showIcon ? 'ml-1' : ''}`}>{title}</span>
                        </>
                    )}
                </button>
            </div>
            {
                show && createPortal(
                    <div ref={panelRef} style={{ position: 'fixed', top: panelPos.y, left: panelPos.x }} className="z-[60]">
                        <div className={`relative max-w-[calc(100vw-16px)] bg-surface-raised dark:bg-stone-600 text-sm text-gray-900 dark:text-gray-100 shadow-xl ring-1 ring-gray-900/5 rounded-lg p-2 ${panelClassName ?? 'w-64'}`}>
                            {!hideArrow && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        width: 10,
                                        height: 10,
                                        transform: 'rotate(45deg)',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    } as React.CSSProperties}
                                    className={`${anchor.openLeft ? 'right-[-5px]' : 'left-[-5px]'} ${anchor.openUp ? 'bottom-4' : 'top-4'} bg-surface-raised dark:bg-stone-600`}
                                />
                            )}
                            {wrapList ? (
                                <ul className="space-y-1">
                                    {children}
                                </ul>
                            ) : children}
                        </div>
                    </div>,
                    document.body
                )
            }
        </>
    )
}